import { useEffect, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Lock } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

// Taking the deposit at signature.
//
// PAYING IS PART OF SIGNING. The card is confirmed first, and only a
// succeeded payment produces the intent id the accept call needs — so a
// declined card leaves no signature behind. That is what the Qwilr pages
// being replaced enforced with `requireOnAccept`, and what the MSA
// describes: 50 per cent upfront, 50 per cent on completion.
//
// The card never touches our server. Stripe's PaymentElement collects it
// inside their iframe and confirms it against a client secret; all we
// ever hold is an intent id, which we then ask Stripe about ourselves
// rather than believing the browser.

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

interface Props {
  token: string;
  depositCents: number;
  balanceCents: number;
  /** Called with the confirmed intent id. The caller then records the signature. */
  onPaid: (paymentIntentId: string) => Promise<void>;
  disabled: boolean;
}

interface IntentResponse {
  clientSecret: string;
  publishableKey: string | null;
  depositCents: number;
  balanceCents: number;
}

/** Cached across mounts — loadStripe injects a script and should run once. */
let stripePromise: Promise<Stripe | null> | null = null;
function stripeFor(publishableKey: string): Promise<Stripe | null> {
  stripePromise ??= loadStripe(publishableKey);
  return stripePromise;
}

export function QuoteDepositForm(props: Props): React.ReactElement {
  const [intent, setIntent] = useState<IntentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch<IntentResponse>(`/api/quotes/${props.token}/payment-intent`, {
          method: 'POST',
          json: {},
        });
        if (!cancelled) setIntent(data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? e.message
              : 'Could not start the payment. Please try again in a moment.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.token]);

  if (loading) {
    return <p className="text-sm text-[var(--color-muted)]">Preparing secure payment…</p>;
  }

  if (error || !intent?.clientSecret || !intent.publishableKey) {
    return (
      <div className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-900">
          {error ??
            'Card payment is unavailable right now. Please reply to the email this quote came in and we will take the deposit another way.'}
        </p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripeFor(intent.publishableKey)}
      options={{
        clientSecret: intent.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#00AEEF',
            colorText: '#2D2D2D',
            colorDanger: '#E53935',
            borderRadius: '8px',
            fontFamily: 'Inter, system-ui, sans-serif',
          },
        },
      }}
    >
      <DepositFields
        depositCents={intent.depositCents}
        balanceCents={intent.balanceCents}
        onPaid={props.onPaid}
        disabled={props.disabled}
      />
    </Elements>
  );
}

function DepositFields({
  depositCents,
  balanceCents,
  onPaid,
  disabled,
}: {
  depositCents: number;
  balanceCents: number;
  onPaid: (paymentIntentId: string) => Promise<void>;
  disabled: boolean;
}): React.ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pay(): Promise<void> {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError(null);
    try {
      // `redirect: 'if_required'` keeps the client on the page for an
      // ordinary card, and still supports the methods that need a
      // redirect. We never leave the signing page for a normal payment.
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message ?? 'The payment could not be completed.');
        return;
      }
      if (result.paymentIntent?.status !== 'succeeded') {
        setError('The payment did not complete. Nothing has been charged.');
        return;
      }

      // Only now is the signature recorded, and the server re-checks this
      // intent with Stripe before it believes us.
      await onPaid(result.paymentIntent.id);
    } catch {
      setError('Something went wrong taking the payment. Nothing has been charged twice.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-sm font-semibold text-[var(--color-header)]">Due today</span>
          <span className="font-display text-[22px] font-bold tabular-nums text-[var(--color-header)]">
            {money(depositCents)}
          </span>
        </div>
        {balanceCents > 0 && (
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {money(balanceCents)} on completion, charged to the same card.
          </p>
        )}
      </div>

      <PaymentElement />

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      <Button onClick={() => void pay()} disabled={disabled || busy || !stripe}>
        {busy ? 'Taking payment…' : `Pay ${money(depositCents)} and accept`}
      </Button>

      <p className="flex items-start gap-2 text-xs text-[var(--color-muted)]">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Card details go straight to Stripe and are never stored by T3. Your card is kept on file for
        the balance, as set out in the Master Services Agreement.
      </p>
    </div>
  );
}
